import { StyleSheet } from "react-native";

/**
 * Mobile-only (iPhone) styles for the Spare Pool Device Management screen.
 * Applied when Platform.OS === "ios" && windowWidth < 768.
 * Does NOT affect web or iPad.
 */
const mobileSparePoolDeviceStyles = StyleSheet.create({
  /* ── Screen root ─────────────────────────────────────────── */
  screenRoot: {
    flex: 1,
    backgroundColor: "#f0f4fa",
  },

  /* ── Header block ────────────────────────────────────────── */
  headerBlock: {
    backgroundColor: "#ffffff",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#ebebeb",
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: "700",
    color: "#1a2332",
    lineHeight: 24,
  },
  headerSubtitle: {
    fontSize: 12,
    color: "#818181",
    marginTop: 2,
  },

  /* ── Info Banner ─────────────────────────────────────────── */
  infoBanner: {
    flexDirection: "row",
    gap: 10,
    alignItems: "flex-start",
    backgroundColor: "#F3F7FD",
    borderWidth: 1,
    borderColor: "#DAEEFA",
    borderRadius: 8,
    padding: 12,
    marginHorizontal: 16,
    marginTop: 12,
  },
  infoText: {
    flex: 1,
    color: "#4B4B4B",
    fontSize: 12,
    lineHeight: 18,
  },

  /* ── Search and Filter bar ───────────────────────────────── */
  toolbarContainer: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#ffffff",
    borderBottomWidth: 1,
    borderBottomColor: "#ebebeb",
    gap: 12,
  },

  searchBox: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#c9d6e5",
    borderRadius: 8,
    paddingHorizontal: 12,
    height: 44,
    backgroundColor: "#ffffff",
  },
  searchInput: {
    flex: 1,
    color: "#2d2d2d",
    fontSize: 14,
    marginLeft: 8,
  },

  filterRow: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },
  filterDropdown: {
    flex: 1,
    minWidth: 140,
  },

  filterButtonRow: {
    flexDirection: "row",
    gap: 8,
  },
  resetButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: "#c9d6e5",
    borderRadius: 6,
    backgroundColor: "#ffffff",
  },
  resetText: {
    color: "#585858",
    fontSize: 12,
    fontWeight: "600",
  },

  /* ── Card list container ─────────────────────────────────── */
  cardList: {
    paddingHorizontal: 16,
    paddingTop: 12,
    paddingBottom: 24,
    gap: 12,
  },

  /* ── Device Card ─────────────────────────────────────────── */
  card: {
    backgroundColor: "#ffffff",
    borderRadius: 10,
    borderWidth: 1,
    borderColor: "#e1eaf5",
    overflow: "hidden",
  },

  cardHeader: {
    backgroundColor: "#f8fafd",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#ebebeb",
  },

  cardTitle: {
    fontSize: 14,
    fontWeight: "700",
    color: "#2d2d2d",
    marginBottom: 4,
  },

  cardSubtitle: {
    fontSize: 12,
    color: "#818181",
  },

  cardContent: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    gap: 12,
  },

  cardRow: {
    gap: 8,
  },

  cardField: {
    marginBottom: 8,
  },

  cardFieldLabel: {
    fontSize: 11,
    fontWeight: "700",
    color: "#818181",
    textTransform: "uppercase",
    letterSpacing: 0.3,
    marginBottom: 4,
  },

  cardFieldValue: {
    fontSize: 13,
    color: "#2d2d2d",
    fontWeight: "500",
  },

  cardFieldMuted: {
    color: "#9099aa",
  },

  /* ── Status and Pills ────────────────────────────────────── */
  statusPill: {
    alignSelf: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    borderRadius: 6,
    paddingHorizontal: 10,
    paddingVertical: 6,
  },

  statusPillActive: {
    backgroundColor: "#ddf6de",
  },

  statusPillInactive: {
    backgroundColor: "#eef2f7",
  },

  statusDot: {
    width: 7,
    height: 7,
    borderRadius: 4,
  },

  statusText: {
    fontSize: 12,
    fontWeight: "700",
  },

  statusTextActive: {
    color: "#008a05",
  },

  statusTextInactive: {
    color: "#9099aa",
  },

  vendorPill: {
    alignSelf: "flex-start",
    backgroundColor: "#e8f1ff",
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },

  vendorPillText: {
    color: "#005cbc",
    fontSize: 11,
    fontWeight: "700",
  },

  capturePill: {
    alignSelf: "flex-start",
    backgroundColor: "#cdecf7",
    borderRadius: 5,
    paddingHorizontal: 10,
    paddingVertical: 5,
  },

  capturePillText: {
    color: "#00749a",
    fontSize: 11,
    fontWeight: "700",
  },

  quantityCapturePill: {
    backgroundColor: "#fff0b8",
  },

  quantityCapturePillText: {
    color: "#c27600",
  },

  /* ── Card Footer / Actions ───────────────────────────────── */
  cardFooter: {
    backgroundColor: "#f8fafd",
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: "#ebebeb",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },

  disabledEditButton: {
    flex: 1,
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 10,
    borderRadius: 6,
    backgroundColor: "#f8fafd",
  },

  disabledEditIcon: {
    color: "#b8c8da",
    marginBottom: 4,
  },

  disabledEditText: {
    color: "#b8c8da",
    fontSize: 11,
    fontWeight: "600",
    textAlign: "center",
  },

  desktopOnlyMessage: {
    fontSize: 10,
    color: "#9099aa",
    textAlign: "center",
    marginTop: 2,
  },

  /* ── Footer Text ─────────────────────────────────────────── */
  footerText: {
    color: "#9099aa",
    fontSize: 12,
    paddingHorizontal: 16,
    paddingVertical: 16,
    textAlign: "center",
  },

  /* ── Empty State ─────────────────────────────────────────── */
  emptyState: {
    alignItems: "center",
    justifyContent: "center",
    paddingVertical: 40,
    paddingHorizontal: 16,
  },

  emptyStateText: {
    color: "#818181",
    fontSize: 14,
    textAlign: "center",
  },
});

export default mobileSparePoolDeviceStyles;
